/**
 * The rules for stopping an account, as pure functions.
 *
 * Stopping is deliberately a DIFFERENT authority model from credit. Credit moves
 * only between an account and its direct parent; a stop may be placed by any
 * strict ancestor at any depth, because a Company Master who sees something
 * wrong five levels down has to stop it now rather than through four
 * intermediaries.
 *
 * Two invariants live here and nowhere else:
 *
 *  1. Effective status is COMPUTED. Suspending an agent stops everything beneath
 *     it, but nothing is ever written onto a descendant — the suspension is
 *     stored on the account actually suspended and every other account derives.
 *     Mass-writing `status` down the subtree would destroy each descendant's own
 *     status permanently, so lifting the suspension could never restore the
 *     branch to what it was.
 *  2. The LIFT RULE. A suspension may be lifted only by the account that placed
 *     it, or by someone above that account. Without it a master under suspicion
 *     simply un-suspends their own downline after the Company Master stopped it,
 *     and the control is worthless.
 *
 * Everything here takes plain values — no world, no store — so the rules can be
 * checked against hand-worked hierarchies the way `book.ts` is checked against
 * hand-worked money.
 */

import type { AccountRecord, AccountStatus, StopControl, StopState, StoppedBy } from '@/data/types'
import { lll } from './format'

/**
 * A stored suspension. One record per account actually stopped — never one per
 * account affected.
 */
export interface Suspension {
  /** The account this was placed ON. */
  accountId: string
  /** The account that placed it. Decides who is allowed to lift it. */
  byId: string
  byUsername: string
  at: string
  /** Always present: "suspicious" is exactly the case where the next person needs
   *  to know what was seen. */
  reason: string
  /**
   * The subject's own status immediately before the stop, restored verbatim when
   * the stop is lifted. An account that was `inactive` before it was stopped
   * must not come back `active`.
   */
  previousStatus: AccountStatus
}

/** The account and its ancestors, ROOT FIRST, ending with the account itself. */
export const chainOf = (account: Pick<AccountRecord, 'id' | 'path'>): string[] => [
  ...account.path,
  account.id,
]

/**
 * True when `callerId` sits strictly above `account`.
 *
 * `path` excludes self, so containment alone already rules out stopping
 * yourself; the explicit id check is kept so a malformed path cannot make an
 * account its own ancestor.
 */
export const isStrictAncestor = (
  callerId: string,
  account: Pick<AccountRecord, 'id' | 'path'>,
): boolean => account.id !== callerId && account.path.includes(callerId)

/**
 * The suspension that actually governs this account: the one CLOSEST TO THE
 * ROOT.
 *
 * When both an ancestor and the account itself are stopped, the ancestor's is
 * the one that has to come off first — lifting the lower record changes nothing
 * an operator can see. Naming the wrong one would send them to lift a stop that
 * is not the one holding the branch down.
 */
export function governingSuspension(
  account: Pick<AccountRecord, 'id' | 'path'>,
  suspensions: ReadonlyMap<string, Suspension>,
): Suspension | null {
  for (const id of chainOf(account)) {
    const found = suspensions.get(id)
    if (found) return found
  }
  return null
}

/** The display projection of a suspension. */
export const stoppedByOf = (
  suspension: Suspension,
  usernameOf: (id: string) => string,
): StoppedBy => ({
  accountId: suspension.accountId,
  username: usernameOf(suspension.accountId),
  byId: suspension.byId,
  byUsername: suspension.byUsername,
  at: suspension.at,
  reason: suspension.reason,
})

/* ------------------------------------------------------------------ copy -- */

/**
 * Every sentence the refusal path can produce, in one place.
 *
 * The data layer throws these and the UI renders them without rewording: a rule
 * explained one way on the button and another way in the error is how an
 * operator concludes the control is broken and stops using it.
 */
export const STOP_COPY = {
  self: 'You cannot stop your own account.',
  notBeneath: 'You can only stop accounts beneath you.',
  /** Absence and no-access must stay indistinguishable — never "forbidden". */
  outside: 'That account is not in your downline.',
  reasonRequired:
    'Give a reason before stopping this account — whoever looks at it next needs to know what you saw.',
  /** Names the account, so the operator knows which record is missing, not that nothing happened. */
  notStopped: (username: string): string => `${username} is not stopped.`,
  /** The lift rule, refused. Names them: the operator's next move is to go and ask. */
  stoppedAbove: (byUsername: string): string =>
    `Stopped by ${byUsername}, above you. Only they can lift it.`,
  /** Stopped by an ancestor rather than directly — lifting THIS row would do nothing. */
  stoppedHigherUp: (ancestorUsername: string): string =>
    `Stopped because ${ancestorUsername} above them is stopped. Lift ${ancestorUsername} to restart this branch.`,
  alreadyStopped: (byUsername: string, at: string): string =>
    `Already stopped by ${byUsername} on ${lll(at)}.`,
  /**
   * The live hierarchy service keeps no suspensions, so an account mapped from
   * it offers no control. Never render a button whose backend cannot enforce it.
   */
  notWiredLive: 'Stopping is not wired to the live hierarchy service yet.',
} as const

/* ---------------------------------------------------------- the projection -- */

export interface StopStateInput {
  account: Pick<AccountRecord, 'id' | 'path' | 'status' | 'username'>
  /** Every suspension currently in force, keyed by the account it was placed on. */
  suspensions: ReadonlyMap<string, Suspension>
  usernameOf: (id: string) => string
  /** The signed-in account. `stopControl` is relative to them, like every read. */
  callerId: string
  /** Every id at or beneath the caller — decides who may lift what. */
  callerSubtree: ReadonlySet<string>
}

/**
 * Effective status, who is stopping it, and the one control the caller may use.
 *
 * `stopControl` mirrors what `setAccountStatus` will actually allow, exactly. A
 * button offered here and refused there — or refused here and allowed there — is
 * worse than no button: the operator learns not to trust either.
 */
export function stopStateOf(input: StopStateInput): StopState {
  const { account, suspensions, usernameOf, callerId, callerSubtree } = input
  const governing = governingSuspension(account, suspensions)
  const own = suspensions.get(account.id) ?? null

  const effectiveStatus: AccountStatus = governing ? 'suspended' : account.status
  const stoppedBy = governing ? stoppedByOf(governing, usernameOf) : null

  const none = (refusal: string): StopControl => ({ action: 'none', refusal })

  // Authority first: what the caller may do to this account at all, before what
  // its current state happens to be.
  if (account.id === callerId) return { effectiveStatus, stoppedBy, stopControl: none(STOP_COPY.self) }
  if (!isStrictAncestor(callerId, account)) {
    return { effectiveStatus, stoppedBy, stopControl: none(STOP_COPY.notBeneath) }
  }

  if (own) {
    // The lift rule. `byId` inside the caller's subtree means the caller is at or
    // above whoever placed it.
    const liftable = own.byId === callerId || callerSubtree.has(own.byId)
    return {
      effectiveStatus,
      stoppedBy,
      stopControl: liftable
        ? { action: 'lift', refusal: null }
        : none(STOP_COPY.stoppedAbove(own.byUsername)),
    }
  }

  if (governing) {
    // Stopped from above. Stopping it again would change nothing an operator can
    // see, and lifting is not this row's to offer.
    return {
      effectiveStatus,
      stoppedBy,
      stopControl: none(STOP_COPY.stoppedHigherUp(usernameOf(governing.accountId))),
    }
  }

  return { effectiveStatus, stoppedBy, stopControl: { action: 'stop', refusal: null } }
}
